import { ReactNode, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Select, Input, message } from "antd";
import { ApolloError } from "@apollo/client";
import { Check } from "lucide-react";

import { useSubmitAdminNavPaywallRequestMutation } from "graphql/mutations/submit-admin-nav-paywall-request.generated";
import routeNames from "constants/route-names";
import { AdminNavPaywallSource, ADMIN_NAV_PAYWALL_BUSINESS_TYPE_OPTIONS } from "constants/constants";

enum GateStep {
	CTA = "cta",
	FORM = "form",
	CONFIRMATION = "confirmation"
}

interface AdminNavPaywallGateProps {
	source: AdminNavPaywallSource;
	icon: ReactNode;
	title: string;
	subtitle: string;
	// Devices has a "What's Included" checklist under the CTA; Intelligence has none.
	includedList?: string[];
}

// Gates a restricted Ollie-profile role's access to an admin nav item (Devices or
// Intelligence) behind a "Contact Sales" CTA (MWA-839/841/842), the same stand-in
// pattern already used for Ollie inspections (see ollie-inspection-gate.tsx).
const AdminNavPaywallGate = ({ source, icon, title, subtitle, includedList }: AdminNavPaywallGateProps) => {
	const navigate = useNavigate();
	const [step, setStep] = useState<GateStep>(GateStep.CTA);

	const [businessType, setBusinessType] = useState<string>();
	const [role, setRole] = useState("");
	const [website, setWebsite] = useState("");
	const [notes, setNotes] = useState("");

	const goToDashboard = useCallback(() => navigate(routeNames.OLLIE_DASHBOARD), [navigate]);

	const [submitAdminNavPaywallRequestMutation, { loading: isSubmitting }] = useSubmitAdminNavPaywallRequestMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only"
	});

	const canSubmit = !!businessType && !!role.trim();

	const handleSubmit = useCallback(async () => {
		if (!canSubmit) return;
		try {
			await submitAdminNavPaywallRequestMutation({
				variables: {
					source,
					business_type: businessType as string,
					role: role.trim(),
					website: website.trim() || null,
					notes: notes.trim() || null
				}
			});
			setStep(GateStep.CONFIRMATION);
		} catch (error) {
			const serverMessage = (error as ApolloError).graphQLErrors?.[0]?.message;
			message.error(serverMessage || "Your request could not be sent. Please try again.");
		}
	}, [canSubmit, submitAdminNavPaywallRequestMutation, source, businessType, role, website, notes]);

	const renderCta = () => (
		<div className="anpg-cta">
			<div className="anpg-icon-badge">
				<span className="anpg-premium-badge">Premium</span>
				{icon}
			</div>
			<h2 className="anpg-title">{title}</h2>
			<p className="anpg-subtitle">{subtitle}</p>
			<div className="anpg-cta-actions">
				<button type="button" className="anpg-btn-primary" onClick={() => setStep(GateStep.FORM)}>
					Contact Sales to Upgrade
				</button>
				<button type="button" className="anpg-btn-secondary" onClick={goToDashboard}>
					Back to Dashboard
				</button>
			</div>
			{includedList && (
				<div className="anpg-included">
					<h4 className="anpg-included-title">What&apos;s Included</h4>
					<ul className="anpg-included-list">
						{includedList.map(item => (
							<li key={item} className="anpg-included-item">
								<span className="anpg-included-check">
									<Check className="anpg-included-check-icon" />
								</span>
								{item}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);

	const renderForm = () => (
		<div className="anpg-form">
			<div className="anpg-form-header">
				<h3 className="anpg-form-title">Tell us a bit more about your company</h3>
			</div>
			<div className="anpg-form-body">
				<div className="anpg-field">
					<label className="anpg-field-label">Business Type</label>
					<Select
						className="anpg-select"
						placeholder="Select business type..."
						value={businessType}
						onChange={setBusinessType}
						options={[...ADMIN_NAV_PAYWALL_BUSINESS_TYPE_OPTIONS]}
						disabled={isSubmitting}
					/>
				</div>
				<div className="anpg-field">
					<label className="anpg-field-label">Your Role</label>
					<Input
						className="anpg-input"
						placeholder="Project Manager, Technician, COO, etc."
						value={role}
						onChange={e => setRole(e.target.value)}
						disabled={isSubmitting}
					/>
				</div>
				<div className="anpg-field">
					<label className="anpg-field-label">Website (Optional)</label>
					<Input
						className="anpg-input"
						placeholder="www.yourcompany.com"
						value={website}
						onChange={e => setWebsite(e.target.value)}
						disabled={isSubmitting}
					/>
				</div>
				<div className="anpg-field">
					<label className="anpg-field-label">Notes (Optional)</label>
					<Input.TextArea
						className="anpg-textarea"
						placeholder="Anything you'd like to ask or mention?"
						value={notes}
						onChange={e => setNotes(e.target.value)}
						autoSize={{ minRows: 3, maxRows: 6 }}
						disabled={isSubmitting}
					/>
				</div>
			</div>
			<div className="anpg-form-footer">
				<button
					type="button"
					className="anpg-btn-primary anpg-submit-btn"
					disabled={!canSubmit || isSubmitting}
					onClick={handleSubmit}
				>
					Submit
				</button>
			</div>
		</div>
	);

	const renderConfirmation = () => (
		<div className="anpg-confirmation">
			<span className="anpg-confirmation-icon">
				<Check className="anpg-confirmation-check-icon" />
			</span>
			<h3 className="anpg-title">Request Sent</h3>
			<p className="anpg-confirmation-text">Thanks for reaching out! Our team will be in touch shortly.</p>
			<button type="button" className="anpg-btn-secondary anpg-confirmation-btn" onClick={goToDashboard}>
				Back to Dashboard
			</button>
		</div>
	);

	return (
		<div className="anpg-page">
			<Modal
				open
				footer={null}
				title={null}
				closable={false}
				maskClosable={false}
				centered
				width={step === GateStep.CTA ? 578 : 448}
				className={`anpg-modal ${step === GateStep.CTA ? "anpg-modal-cta" : ""}`}
			>
				{step === GateStep.CTA && renderCta()}
				{step === GateStep.FORM && renderForm()}
				{step === GateStep.CONFIRMATION && renderConfirmation()}
			</Modal>
		</div>
	);
};

export default AdminNavPaywallGate;
